import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, MessageCircle, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { buildWhatsAppUrl } from "@/lib/helpers";
import ProductCard from "@/components/ProductCard";

const ALL = "all";
const PRICE_RANGES = {
  under50: { min: 0, max: 49999, label: "Di bawah Rp50rb" },
  "50-100": { min: 50000, max: 99999, label: "Rp50rb – Rp100rb" },
  "100-200": { min: 100000, max: 199999, label: "Rp100rb – Rp200rb" },
  over200: { min: 200000, max: null, label: "Di atas Rp200rb" },
};

export default function Catalog() {
  const { settings } = useStore();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");

  const category = params.get("category") || ALL;
  const brand = params.get("brand") || ALL;
  const model = params.get("model") || ALL;
  const q = params.get("q") || "";
  const price = params.get("price") || ALL;
  const page = parseInt(params.get("page") || "1", 10);

  const update = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v && v !== ALL) next.set(k, v);
        else next.delete(k);
      });
      if (!("page" in patch)) next.delete("page");
      return next;
    }, { replace: false });
  }, [setParams]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (brand === ALL) { setModels([]); return; }
    api.get("/phone-models", { params: { brand_id: brand } }).then((r) => setModels(r.data)).catch(() => {});
  }, [brand]);

  useEffect(() => {
    setLoading(true);
    const p = { page, page_size: 200 };
    if (category !== ALL) p.category_id = category;
    if (brand !== ALL) p.brand_id = brand;
    if (model !== ALL) p.model_id = model;
    if (q) p.q = q;
    const range = PRICE_RANGES[price];
    if (range) {
      if (range.min !== null && range.min !== undefined) p.min_price = range.min;
      if (range.max !== null && range.max !== undefined) p.max_price = range.max;
    }
    api.get("/products", { params: p })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, page: 1, total_pages: 1 }))
      .finally(() => setLoading(false));
  }, [category, brand, model, q, price, page]);

  useEffect(() => {
    if (model !== ALL || q) api.post("/track/search", { query: q, model_id: model !== ALL ? model : null }).catch(() => {});
  }, [model, q]);

  const submitSearch = (e) => { e.preventDefault(); update({ q: searchInput }); };
  const clearFilters = () => { setSearchInput(""); setParams(new URLSearchParams(), { replace: false }); };

  const askAdmin = () => {
    const text = `Halo Admin ${settings?.store_name}, saya mencari produk untuk tipe HP tertentu tapi belum tersedia di katalog. Bisa dibantu ketersediaannya?`;
    window.open(buildWhatsAppUrl(settings?.whatsapp_number, text), "_blank");
  };

  const hasFilters = category !== ALL || brand !== ALL || model !== ALL || price !== ALL || q;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Katalog Produk</h1>
      <p className="mt-1 text-sm text-muted-foreground">Pilih kategori dan tipe HP untuk melihat produk yang kompatibel.</p>

      {/* FILTERS */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold"><SlidersHorizontal className="h-4 w-4 text-accent" /> Filter</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select value={category} onValueChange={(v) => update({ category: v })}>
            <SelectTrigger data-testid="category-filter-select"><SelectValue placeholder="Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Semua Kategori</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={brand} onValueChange={(v) => update({ brand: v, model: ALL })}>
            <SelectTrigger data-testid="brand-filter-select"><SelectValue placeholder="Merek HP" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Semua Merek</SelectItem>
              {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={model} onValueChange={(v) => update({ model: v })} disabled={brand === ALL}>
            <SelectTrigger data-testid="model-filter-select"><SelectValue placeholder={brand === ALL ? "Pilih merek dulu" : "Tipe HP"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Semua Tipe</SelectItem>
              {models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={price} onValueChange={(v) => update({ price: v })}>
            <SelectTrigger data-testid="price-filter-select"><SelectValue placeholder="Harga" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Semua Harga</SelectItem>
              {Object.entries(PRICE_RANGES).map(([k, r]) => <SelectItem key={k} value={k}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <form onSubmit={submitSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="product-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cari nama produk, kode, merek, atau tipe HP..."
              className="pl-9"
            />
          </div>
          <button type="submit" data-testid="search-submit-btn" className="rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-90">Cari</button>
          {hasFilters && (
            <button type="button" data-testid="clear-filters-btn" onClick={clearFilters} className="flex items-center gap-1 rounded-xl border border-border px-3 text-sm hover:bg-secondary">
              <X className="h-4 w-4" /> Reset
            </button>
          )}
        </form>
      </div>

      {/* RESULTS */}
      <div className="mt-6">
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}
          </div>
        ) : data.items.length === 0 ? (
          <div data-testid="empty-state" className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
            <p className="font-heading text-lg font-semibold">Produk untuk tipe HP ini belum tersedia.</p>
            <p className="mt-1 text-sm text-muted-foreground">Hubungi admin untuk menanyakan ketersediaannya.</p>
            <button data-testid="ask-admin-btn" onClick={askAdmin} className="mt-4 inline-flex items-center gap-2 rounded-full bg-whatsapp px-6 py-3 text-sm font-semibold text-white hover:opacity-90">
              <MessageCircle className="h-4 w-4" /> Tanya Admin via WhatsApp
            </button>
          </div>
        ) : (
          <>
            <p className="mb-3 text-sm text-muted-foreground">{data.total} produk ditemukan</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
              {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
